'use client';

import { useCallback, useEffect, useRef } from 'react';
import { Gradient } from './Gradient';
import styles from './SlowTextSection.module.css';

const getTranslateGivenProgressionInViewPort = ({
  progression,
}: {
  progression: number;
}) => {
  const maxTransform = 300;
  const startBreakpoint = 0;
  const endBreakpoint = 1;

  if (progression < startBreakpoint) {
    return maxTransform;
  }
  const newProgression =
    (progression - startBreakpoint) / (endBreakpoint - startBreakpoint);
  const value = maxTransform - maxTransform * newProgression;
  return Math.min(Math.max(0, value), maxTransform);
};

const getOpacityGivenProgressionInViewPort = ({
  progression,
}: {
  progression: number;
}) => {
  const startBreakpoint = 0.1;
  const endBreakpoint = 0.1;
  if (progression < startBreakpoint) {
    return 0;
  }
  const newProgression =
    (progression - startBreakpoint) / (endBreakpoint - startBreakpoint);
  return Math.min(newProgression, 1);
};

export const SlowTextSection = ({ className }: { className?: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const gradientRef = useRef<HTMLDivElement>(null);
    const latestAnimationFrame = useRef<number | null>(null);
    const intersectionObserver = useRef<IntersectionObserver | null>(null);

    const animate = useCallback(() => {
      if (containerRef.current && contentRef.current && gradientRef.current) {
        const containerBoundingRect =
          containerRef.current.getBoundingClientRect();
        const posTopContainerToViewport = containerBoundingRect.top;
        const height = Math.max(
          containerBoundingRect.height,
          window.innerHeight,
        );
        const progression = Math.max(
          0,
          -(posTopContainerToViewport - window.innerHeight) / height,
        );

        contentRef.current.style.transform = `translateY(${getTranslateGivenProgressionInViewPort(
          { progression },
        )}px)`;
        contentRef.current.style.opacity = `${getOpacityGivenProgressionInViewPort(
          {
            progression,
          },
        )}`;
        gradientRef.current.style.opacity = '1';
        if (progression > 0.8) {
          gradientRef.current.style.opacity = `${1 - (progression - 0.8)}`;
        }
      }
      latestAnimationFrame.current = requestAnimationFrame(animate);
    }, []);

    const startAnimation: IntersectionObserverCallback = (entries) => {
      const entry = entries[0];

      if (entry && entry.isIntersecting && !latestAnimationFrame.current) {
        latestAnimationFrame.current = requestAnimationFrame(animate);
        return;
      }
      if (latestAnimationFrame.current) {
        cancelAnimationFrame(latestAnimationFrame.current);
      }
      latestAnimationFrame.current = null;
    };

    useEffect(() => {
      intersectionObserver.current = new IntersectionObserver(startAnimation, {
        root: null,
        rootMargin: '0px',
        threshold: 0.01,
      });
      if (containerRef.current) {
        intersectionObserver.current.observe(containerRef.current);
      }
      return () => {
        if (latestAnimationFrame.current) {
          cancelAnimationFrame(latestAnimationFrame.current);
        }
        intersectionObserver.current?.disconnect();
      };
    }, []);

  return (
    <div
      className={`${styles.root} u-grid${className ? ` ${className}` : ''}`}
      ref={containerRef}
    >
      <div className={styles.content} ref={contentRef}>
        <h1>
          Learn
          <br />
          from the best
        </h1>
        <h2>
          Learn from 100+ of the world&apos;s best athletes, neuroscience
          experts and psychologists.
        </h2>
        <h2>
          Unique integrated approach that combines the science of workout,
          mindfulness and sleep in one platform.
        </h2>
      </div>
      <Gradient ref={gradientRef} />
    </div>
  );
};
